import { Localized, useLocalization } from "@fluent/react";
import { Button, ButtonGroup, Card, CardContent, Collapse, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SyncIcon from "@mui/icons-material/Sync";
import { useCallback, useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingContainer } from "../../app/LoadingContainer";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { MonthFilterMenu } from "../documents/utils/MonthFilterMenu";
import { selectSettings } from "../settings/settingsSlice";
import { showError } from "../notifications/notificationsSlice";
import { AICallMetrics, AIRequestTrace, fetchAIRequestsForCompany, fetchAITraceForCompany } from "./aiRequestsApi";

export const ClientAIRequests = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch();

    const [requests, setRequests] = useState<AICallMetrics[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedRow, setSelectedRow] = useState<AICallMetrics | null>(null);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [traceData, setTraceData] = useState<AIRequestTrace | null>(null);
    const [traceLoading, setTraceLoading] = useState(false);
    const [expandedMessage, setExpandedMessage] = useState(false);
    const [expandedResponse, setExpandedResponse] = useState(false);

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;
        if (locale === "pl") {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        }
        return defaultEnLocaleText;
    };

    const fetchUsageData = useCallback(async (dateFromParam?: Date | null, dateToParam?: Date | null) => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchAIRequestsForCompany({
                dateFrom: dateFromParam !== undefined ? dateFromParam || undefined : dateFrom || undefined,
                dateTo: dateToParam !== undefined ? dateToParam || undefined : dateTo || undefined,
            }));

            if (fetchAIRequestsForCompany.fulfilled.match(result)) {
                setRequests(result.payload as AICallMetrics[]);
            } else if (fetchAIRequestsForCompany.rejected.match(result)) {
                dispatch(showError("error"));
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, dateFrom, dateTo]);

    useEffect(() => {
        fetchUsageData(undefined, undefined);
    }, [fetchUsageData]);

    useEffect(() => {
        setExpandedMessage(false);
        setExpandedResponse(false);
    }, [traceData]);

    const handleRefreshClick = () => {
        setSelectedRow(null);
        fetchUsageData(dateFrom, dateTo);
    };

    const handleMonthFilterSubmit = (dates: { dateFrom: Date; dateTo: Date }) => {
        setDateFrom(dates.dateFrom);
        setDateTo(dates.dateTo);
    };

    const handleShowTrace = useCallback(async (traceId: string) => {
        setTraceLoading(true);
        try {
            const result = await dispatch(fetchAITraceForCompany(traceId));
            if (fetchAITraceForCompany.fulfilled.match(result)) {
                setTraceData(result.payload);
                const requestRow = requests.find((stat) => stat.traceId === traceId);
                if (requestRow) {
                    setSelectedRow(requestRow);
                }
            } else {
                dispatch(showError("error"));
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setTraceLoading(false);
        }
    }, [dispatch, requests]);

    const requestColumns: GridColDef<AICallMetrics>[] = [
        {
            field: "userId",
            headerName: l10n.getString("user", null, "_Użytkownik"),
            flex: 1
        },
        {
            field: "occurredAt",
            headerName: l10n.getString("date", null, "_Data"),
            flex: 1,
            valueFormatter: (value) => {
                if (!value) {
                    return "";
                }
                return new Date(value as string).toLocaleString(locale);
            }
        },
        {
            field: "inputTokens",
            headerName: "INPUT",
            width: 80
        },
        {
            field: "outputTokens",
            headerName: "OUTPUT",
            width: 80
        },
        {
            field: "totalTokens",
            headerName: "TOTAL",
            width: 80
        },
        {
            field: "actions",
            type: "actions",
            width: 220,
            renderCell: (params) => (
                <ButtonGroup>
                    <Button
                        color="primary"
                        onClick={() => {
                            setSelectedRow(params.row);
                            if (params.row.traceId) {
                                handleShowTrace(params.row.traceId);
                            }
                        }}
                    >
                        <Localized id="ai-usage-show-trace">
                            <span>_Pokaż szczegóły</span>
                        </Localized>
                    </Button>
                </ButtonGroup>
            )
        }
    ];

    if (isLoading) {
        return <LoadingContainer />;
    }

    return (
        <>
            <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                <MonthFilterMenu
                    initialDateFrom={dateFrom}
                    initialDateTo={dateTo}
                    onSubmit={handleMonthFilterSubmit}
                />
                <IconButton onClick={handleRefreshClick}>
                    <SyncIcon />
                </IconButton>
            </Stack>

            <Grid container spacing={2} sx={{ height: "calc(100vh - 280px)" }}>
                <Grid item xs={selectedRow ? 6 : 12} sx={{ height: "100%" }}>
                    <DataGrid
                        style={{ height: "100%" }}
                        localeText={getCustomLocaleText()}
                        rows={requests}
                        columns={requestColumns}
                        getRowId={(row) => row.id}
                        onRowClick={(params) => setSelectedRow(params.row)}
                        slots={{ toolbar: QuickSearchToolbar }}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "occurredAt", sort: "desc" }],
                            },
                        }}
                    />
                </Grid>
                {selectedRow && (
                    <Grid item xs={6} sx={{ height: "100%", overflow: "auto", padding: 2 }}>
                        <Paper sx={{ padding: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                                <Typography variant="h6">{selectedRow.userId}</Typography>
                                <IconButton size="small" onClick={() => setSelectedRow(null)}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                            {traceLoading ? (
                                <LoadingContainer />
                            ) : traceData ? (
                                <Stack spacing={2}>
                                    <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5" }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    <Localized id="ai-message">_Wiadomość:</Localized>
                                                </Typography>
                                                <IconButton size="small" onClick={() => setExpandedMessage(!expandedMessage)}>
                                                    {expandedMessage ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Stack>
                                            <Collapse in={expandedMessage} timeout={0}>
                                                <Typography variant="body2" sx={{ marginTop: 1, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                                    {traceData.systemPrompt}
                                                </Typography>
                                                <Typography variant="body2" sx={{ marginTop: 1, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                                    {traceData.userMessage}
                                                </Typography>
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                    <Card variant="outlined" sx={{ backgroundColor: "#f0f8f4" }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    <Localized id="ai-response">_Odpowiedź:</Localized>
                                                </Typography>
                                                <IconButton size="small" onClick={() => setExpandedResponse(!expandedResponse)}>
                                                    {expandedResponse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Stack>
                                            <Collapse in={expandedResponse} timeout={0}>
                                                <Typography variant="body2" sx={{ marginTop: 1, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                                    {traceData.responseText}
                                                </Typography>
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                </Stack>
                            ) : (
                                <Typography>
                                    <Localized id="ai-select-trace">_Wybierz wiersz, aby wyświetlić szczegóły</Localized>
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </>
    );
};
